import { ApiPermissionOptions } from "api/client.generated";
import LoadingScreen from "components/primitives/LoadingScreen";
import usePermissions from "hooks/api/usePermissions";
import useRouteDetector from "hooks/useRouteDetector";
import { createContext, useContext, useMemo } from "react";

interface IApiPermissionsContext {
  apiPermissions: ApiPermissionOptions[];
}

const ApiPermissionsContext = createContext<IApiPermissionsContext | null>(null);

interface ApiPermissionsProviderProps {
  children: React.ReactElement;
}

export const ApiPermissionsContextProvider = ({ children }: ApiPermissionsProviderProps) => {
  const { isSignInRoute, isSelectPartnerRoute } = useRouteDetector();
  const { permissions, isPermissionsFetching } = usePermissions(
    !isSignInRoute && !isSelectPartnerRoute,
  );

  const ctxValue = useMemo<IApiPermissionsContext>(
    () => ({ apiPermissions: permissions ?? [] }),
    [permissions],
  );

  if (isPermissionsFetching) return <LoadingScreen />;

  // prettier-ignore
  return (
    <ApiPermissionsContext.Provider value={ctxValue}>
      {children}
    </ApiPermissionsContext.Provider>
  );
};

export const useApiPermissionsContext = (): IApiPermissionsContext => {
  const context = useContext(ApiPermissionsContext);
  if (!context) {
    throw new Error("useApiPermissionsContext must be used within a ApiPermissionsContextProvider");
  }

  return context;
};
